import * as _ from "lodash";
import { convertLocaltoUTCFormat } from "../../../../services/DateFormatUtility";
import { Tooltip, Zoom } from "@mui/material";
import PropTypes from "prop-types";

export const BDN_OPTIONS = [
	{
		label: "BDN",
		value: "BDN",
	},
	{
		label: "COR",
		value: "COR",
	},
];
export const BDN_HEADER = "BDN";

const RenderCell = ({ renderedCellValue, row, column }) => {
	const validationMessage = row?.original?._validationMessage;

	if (!validationMessage) {
		return <span>{renderedCellValue}</span>;
	}

	return (
		<CustomTooltip title={validationMessage}>
			<div
				style={{
					width: "100%",
					minHeight: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: column?.id === "dateOfOperation" ? "center" : "flex-start",
				}}
			>
				{renderedCellValue || "\u00A0"}
			</div>
		</CustomTooltip>
	);
};

const genericColumns = [
	{
		header: "Date of Operation",
		accessorKey: "dateOfOperation",
		size: 150,
		muiTableHeadCellProps: {
			align: "center",
		},

		muiEditTextFieldProps: {
			type: "datetime-local",
		},
		accessorFn: (row) => {
			if (row.dateOfOperation) {
				return convertLocaltoUTCFormat(row.dateOfOperation);
			}
		},
		muiTableBodyCellProps: {
			align: "center",
		},
		Cell: RenderCell,
	},
	{
		header: "BDN TYPE",
		accessorKey: "bdnType",
		filterVariant: "select",
		filterSelectOptions: ["BDN", "COR"],
		Cell: RenderCell,
	}
];

const columnFuelTypeId = {
	header: "FUEL TYPE ID",
	accessorKey: "fuelTypeId",
	Cell: RenderCell,
};

const columnRemarks = {
	header: "Remarks",
	accessorKey: "remarks",
	Cell: RenderCell,
};

const getColumnsDefinition = (bdnColumns) => {
	const bdnCols = bdnColumns.map((item) => {
		if (item.accessorKey) {
			const column = {
				header: item.headerName,
				accessorKey: item.accessorKey,
				aggregationFn: "sum",
				Cell: RenderCell,
			};
			return column;
		}
	});

	if (!bdnCols) return [];

	const allColumns = [...genericColumns, ...bdnCols, columnFuelTypeId, columnRemarks];
	return allColumns;
};

// Function to transform BDN data into the format required by the table
// It takes the BDN data, validation messages, and validation types as input
export const tranformBdnData = (bdnData, validationMessages, validateTypes) => {
	if (!bdnData) return { columns: [], data: [] };

	bdnData.forEach((item) => {
		if (item.values) {
			item.accessorKey = item.accessorKey ?? _.camelCase(item.headerName);
		}
	});

	const bdnColumns = bdnData.filter((x) => x.accessorKey?.includes("Bdn"));
	if (bdnColumns.length === 0) return { columns: [], data: [] };

	const tableColumns = getColumnsDefinition(bdnColumns);

	const extractRowId = (key) => {
		if (key == null) return null;
		const str = String(key);
		if (str.includes("|")) return str.split("|")[1];
		const numericMatch = str.match(/\d+/g);
		return numericMatch ? numericMatch[numericMatch.length - 1] : str;
	};

	const rowsById = {};
	const rowOrder = [];

	bdnData.forEach((column) => {
		const accessorKey = column.accessorKey;
		const rawValues = column.values;
		if (!accessorKey || !rawValues) return;

		Object.entries(rawValues).forEach(([key, value]) => {
			if (key === "0") return;
			const rowId = extractRowId(key);
			if (!rowId) return;

			if (!rowsById[rowId]) {
				rowsById[rowId] = { Id: rowId };
				rowOrder.push(rowId);
			}

			rowsById[rowId][accessorKey] = value;
		});
	});

	const result = rowOrder.map((rowId) => rowsById[rowId]);

	// Add messages (tooltip) and validation types (Row color) to items
	result.forEach((item) => {
		if (validationMessages?.hasOwnProperty(item.Id)) {
			item._validationMessage = validationMessages[item.Id];
		}

		if (validateTypes?.hasOwnProperty(item.Id)) {
			item._validateType = validateTypes[item.Id];
		}
	});

	return { columns: tableColumns, data: result };
};

export const CustomTooltip = ({ title, children }) => (
	<Tooltip
		title={<div style={{ whiteSpace: "pre-line" }}>{title}</div>}
		placement="bottom"
		TransitionComponent={Zoom}
		followCursor={true}
		arrow
		style={{ width: "100%", height: "100%" }}
		componentsProps={{
			tooltip: {
				sx: {
					maxWidth: "800px",
					padding: "8px",
				},
			},
		}}
	>
		{children}
	</Tooltip>
);

RenderCell.propTypes = {
	renderedCellValue: PropTypes.any.isRequired,
	row: PropTypes.shape({
		original: PropTypes.shape({
			_validationMessage: PropTypes.string,
		}),
	}),
};

CustomTooltip.propTypes = {
	title: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};